import { ClinicalAllergy, ClinicalCondition, MemberClinicalData } from '../types';

/**
 * A member with no linked DrChrono patient still gets a 200, with empty lists and a null
 * `last_synced_at` — the sync time is the only thing that says an EHR record exists.
 */
export const hasEhrRecord = (data: MemberClinicalData | undefined): data is MemberClinicalData =>
    !!data?.last_synced_at;

// DrChrono statuses that mean the condition still needs attention. Anything else
// (resolved, inactive, remission, unknown) ranks after these.
const ONGOING_CONDITION_STATUSES = ['active', 'chronic', 'recurrence', 'relapse'];

const isOngoing = (status: string | null) =>
    ONGOING_CONDITION_STATUSES.includes(status?.toLowerCase() ?? '');

const conditionDate = (condition: ClinicalCondition) =>
    Date.parse(condition.date_diagnosis ?? condition.date_onset ?? '') || 0;

/**
 * The records have no priority field, so "top" means: not removed, ongoing before the
 * rest, then most recently diagnosed (or onset) first.
 */
export const getTopConditions = (conditions: ClinicalCondition[], limit = 3) => {
    const current = conditions.filter((condition) => !condition.removed_at);

    return {
        items: [...current]
            .sort(
                (a, b) =>
                    Number(isOngoing(b.status)) - Number(isOngoing(a.status)) ||
                    conditionDate(b) - conditionDate(a)
            )
            .slice(0, limit),
        total: current.length
    };
};

/** Not removed, active first; otherwise the API's order (allergies carry no dates). */
export const getTopAllergies = (allergies: ClinicalAllergy[], limit = 3) => {
    const current = allergies.filter((allergy) => !allergy.removed_at);
    const isActive = (allergy: ClinicalAllergy) => allergy.status?.toLowerCase() === 'active';

    return {
        items: [...current]
            .sort((a, b) => Number(isActive(b)) - Number(isActive(a)))
            .slice(0, limit),
        total: current.length
    };
};
